import React, { useRef, useEffect, useState } from "react";
import cx from "classnames";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Divider from "./Divider";
import SubmitBtn from "./SubmitBtn";

let clickTime = 0;
let activeDropzone = null;
let isDividerMoved = false;

const usePreviousValue = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export default function SyllableSegmentationContent({
  word,
  correctWord,
  showAnswer,
  interactive,
  handleGuess,
  currentPuzzleEnded,
  preview,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const correctTargetRef = useRef([]);
  const allOptionsRef = useRef([]);
  const lettersRef = useRef(null);
  const [mappedWord, setMappedWord] = useState([]);
  const [dividers, setDividers] = useState(
    Array(2)
      .fill("")
      .map((i, index) => ({
        id: "divider_" + index + "_" + word,
        visible: true,
      }))
  );
  const [activeDivider, setActiveDivider] = useState(null);
  const [dropzoneVisible, toggleDropzoneVisibility] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const prevWord = usePreviousValue(word);

  useEffect(() => {
    if (!interactive) {
      toggleDropzoneVisibility(false);
      setIsDragging(false);
      setActiveDivider(null);
    }
  }, [interactive]);

  useEffect(() => {
    if (dropzoneVisible) {
      if (activeDropzone === "BANK") {
        const dropzones = lettersRef.current.querySelectorAll(".dropzone");
        for (let dropzone of dropzones) {
          if (dropzone.getAttribute("tabindex").toString() !== "-1") {
            dropzone.focus();
            break;
          }
        }
        activeDropzone = "";
      }
    }
  }, [dropzoneVisible]);

  useEffect(() => {
    if (interactive) {
      const onKeyDown = (e) => {
        if (e.key === "Escape") {
          setActiveDivider(null);
          toggleDropzoneVisibility(false);
        }
      };
      setIsVisible(true);
      window.addEventListener("pointerdown", onMouseTouchUp);
      window.addEventListener("keydown", onKeyDown);
      return () => {
        window.removeEventListener("pointerdown", onMouseTouchUp);
        window.removeEventListener("keydown", onKeyDown);
      };
    }
  }, [interactive]);

  useEffect(() => {
    const processWord = (isSameWord) => {
      const convertedWord = word.split("").map((letter, index) => {
        return {
          id: letter === "/" ? `divider_${index}` : `letter_${letter}_${index}`,
          content: letter,
        };
      });
      setMappedWord(convertedWord);
      if (!isSameWord || (isSameWord && !preview)) {
        allOptionsRef.current = [];
        const timestamp = new Date().getTime();
        setDividers(
          Array(convertedWord.length - 1)
            .fill("")
            .map((i, index) => ({
              id: "divider_" + index + "_" + word + timestamp,
              visible: true,
            }))
        );
      }
    };
    if (word && word.length > 0) {
      const isSameWord =
        prevWord && prevWord.replaceAll("/", "") === word.replaceAll("/", "");
      if (isVisible && !isSameWord) {
        setIsVisible(false);
      }
      if (isSameWord) {
        processWord(isSameWord);
      } else {
        setTimeout(() => {
          processWord(isSameWord);
          setIsVisible(true);
        });
      }
    }
  }, [word]);

  useEffect(() => {
    if (
      correctWord &&
      correctWord.length > 0 &&
      correctTargetRef.current.length > 0 &&
      mappedWord.map((i) => i.content).includes("/") &&
      !showAnswer
    ) {
      correctTargetRef.current.forEach((optionRef, index) => {
        const targetRect = allOptionsRef.current[index].getBoundingClientRect();
        const targetX = targetRect.left;
        const targetY = targetRect.top;

        const startingRect = optionRef.getBoundingClientRect();
        const startingX = startingRect.left + window.scrollX;
        const startingY = startingRect.top + window.scrollY;

        const deltaX =
          targetX - startingX - (startingRect.width - targetRect.width) / 2;
        const deltaY =
          targetY - startingY - (startingRect.height - targetRect.height) / 2;

        optionRef.style.transition = "transform 1.5s";
        optionRef.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
        optionRef.style.zIndex = 1000;
      });

      return;
    }
    if (showAnswer && correctTargetRef.current.length > 0) {
      setDividers([
        ...dividers.map((d) => {
          d.visible = true;
          return d;
        }),
      ]);
      correctTargetRef.current.forEach((optionRef) => {
        // Move the correct option to the correct target
        const targetRect = optionRef.getBoundingClientRect();
        const targetX = targetRect.left;
        const targetY = targetRect.top;

        const correctOptionRef =
          allOptionsRef.current[allOptionsRef.current.length - 1];

        if (correctOptionRef) {
          const startingRect = correctOptionRef.getBoundingClientRect();
          const startingX = startingRect.left + window.scrollX;
          const startingY = startingRect.top + window.scrollY;

          const deltaX =
            targetX - startingX + (targetRect.width - startingRect.width) / 2;
          const deltaY =
            targetY - startingY + (targetRect.height - startingRect.height) / 2;

          correctOptionRef.style.transition = "transform 1.5s";
          correctOptionRef.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
        }
      });
    }
  }, [showAnswer, correctWord, correctTargetRef.current.length > 0]);

  const onMouseTouchDown = (id) => {
    if (!interactive || currentPuzzleEnded || isDividerMoved) {
      return;
    }
    if (!showAnswer && !isDragging) {
      setActiveDivider(id);
      toggleDropzoneVisibility(true);
      clickTime = new Date().getTime();
    }
  };

  const onMouseTouchUp = (e) => {
    if (!interactive || currentPuzzleEnded) {
      setActiveDivider(null);
      return;
    }
    let found = false;
    let foundCorrectTarget = false;
    if (e.target) {
      allOptionsRef.current.forEach((ref) => {
        if (ref && ref.contains(e.target)) {
          found = true;
        }
      });
      if (lettersRef.current.contains(e.target)) {
        found = true;
        foundCorrectTarget = false;
      }

      if (!found || e.target.classList.contains("dropzone")) {
        clickTime = 0;
      }
    }
    if (!clickTime) {
      toggleDropzoneVisibility(false);
      setTimeout(() => {
        setActiveDivider(null);
      });
      return;
    }
    if (foundCorrectTarget) {
      return;
    }
    if (!isDragging && !dropzoneVisible) {
      toggleDropzoneVisibility(true);
    }

    setTimeout(() => {
      setIsDragging(false);
    });
  };

  const handleKeyDown = (e, id) => {
    if (currentPuzzleEnded || !interactive) {
      return;
    }
    if (e.key === "Enter") {
      e.stopPropagation();
      onMouseTouchDown(id);
      onMouseTouchUp(e);
      if (!lettersRef.current.contains(e.target)) {
        activeDropzone = "BANK";
      } else {
        e.target.style.opacity = 0.3;
      }
    } else if (e.key === "Escape") {
      if (lettersRef.current.contains(e.target)) {
        e.target.style.opacity = 1;
      }
    }
  };

  function getDraggableStyle(style, snapshot) {
    if (snapshot.isDragging) {
      return style;
    }
    if (!snapshot.isDropAnimating) {
      return { ...style, transform: "translate(0px, 0px)" };
    }

    const { moveTo } = snapshot.dropAnimation;
    // move to the right spot
    const translate = `translate(${moveTo.x + 75}px, ${moveTo.y + 55}px)`;

    // patching the existing style
    return {
      ...style,
      transform: `${translate}`,
    };
  }

  const onDragStart = () => {
    clickTime = 0;
    setIsDragging(true);
  };

  const onDragEnd = (result) => {
    setActiveDivider(null);
    setIsDragging(false);
    toggleDropzoneVisibility(false);

    if (
      result &&
      result.destination &&
      result.destination.droppableId !== result.source.droppableId
    ) {
      const filteredMappedWord = [...mappedWord].filter(
        (letter) => letter.id !== result.source.droppableId
      );
      const index = filteredMappedWord.findIndex(
        (letter) => letter.id === result.destination.droppableId
      );
      if (index >= 0) {
        let convertedWord = [...filteredMappedWord];
        convertedWord.splice(index + 1, 0, {
          content: "/",
          id: result.draggableId,
        });

        setMappedWord(convertedWord);
        setDividers(
          dividers.map((d) => {
            if (d.id === result.draggableId) {
              d.visible = false;
            }
            return d;
          })
        );
      } else if (result.source.droppableId.includes("divider")) {
        setMappedWord(
          mappedWord.filter((letter) => letter.id !== result.source.droppableId)
        );
        setDividers(
          dividers.map((d, index) => {
            if (d.id === result.source.droppableId) {
              d.visible = true;
              d.id += index;
            }
            return d;
          })
        );
      }
    }
  };

  const handleMoveDivider = (target, index = -1) => {
    if (activeDivider) {
      isDividerMoved = true;
      if (index >= 0) {
        const currentIndex = target.findIndex((l) => l.id === activeDivider);
        if (currentIndex >= 0) {
          // divider is inside the word, but changing the position
          const element = target.splice(currentIndex, 1)[0];
          target.splice(currentIndex > index ? index + 1 : index, 0, element);
        } else {
          // divider is in the options, moving it to the word
          const element = { content: "/", id: activeDivider };
          target.splice(index + 1, 0, element);
          setDividers((prevState) =>
            prevState.map((d) => {
              if (d.id === activeDivider) {
                d.visible = false;
              }
              return d;
            })
          );
        }

        setMappedWord([...target]);
      } else {
        // divider is in the word, moving it to the options
        setDividers(
          target.map((d, i) => {
            if (d.id === activeDivider) {
              d.visible = true;
              d.id += i;
            }
            return d;
          })
        );
        setMappedWord((prevState) =>
          prevState.filter((l) => l.id !== activeDivider)
        );
      }

      setTimeout(() => {
        isDividerMoved = false;
      }, 300);
    }
  };

  const handleKeyDownDrop = (e, target, index) => {
    if (e.key === "Enter") {
      handleMoveDivider(target, index);
      setActiveDivider(null);
      toggleDropzoneVisibility(false);
    }
  };

  const submitAnswer = () => {
    const guessWord = mappedWord.map((letter) => letter.content).join("");
    handleGuess(guessWord);
  };

  const handleOptionClick = (e) => {
    if (currentPuzzleEnded || !interactive) {
      return;
    }
    e.stopPropagation();
  };

  const renderTestWord = (word) => {
    const wordItemClasses = cx({
      "letter-item": true,
    });

    let dividerIndex = 0;
    correctTargetRef.current = [];

    return word.map((letter, index) => {
      if (letter.content === "/") {
        return (
          <div
            ref={(el) => {
              if (el) {
                correctTargetRef.current[dividerIndex] = el;
                dividerIndex++;
              }
            }}
            key={letter.id + "_" + index.toString()}
          >
            <Droppable
              isDropDisabled={
                index === word.length - 1 ||
                letter.id.includes("divider") ||
                word[index + 1].id.includes("divider") ||
                currentPuzzleEnded
              }
              droppableId={letter.id}
            >
              {(providedDroppable) => (
                <div
                  ref={providedDroppable.innerRef}
                  {...providedDroppable.droppableProps}
                  className="divider-container"
                >
                  <Draggable
                    key={letter.id + index}
                    draggableId={letter.id}
                    index={index}
                    isDragDisabled={currentPuzzleEnded || !interactive}
                  >
                    {(provided, snapshot) => (
                      <div
                        onPointerUp={() => {
                          onMouseTouchDown(letter.id);
                        }}
                        onKeyDown={(e) => {
                          handleKeyDown(e, letter.id);
                        }}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        ref={provided.innerRef}
                        style={getDraggableStyle(
                          provided.draggableProps.style,
                          snapshot
                        )}
                        tabIndex={-1}
                      >
                        <div
                          className={cx({
                            "letter-item": true,
                            visible: showAnswer,
                            disabled: currentPuzzleEnded || !interactive,
                          })}
                          tabIndex={
                            activeDivider || currentPuzzleEnded || !interactive
                              ? -1
                              : 0
                          }
                        >
                          <Divider
                            visible={!showAnswer}
                            dividerClasses={cx({
                              active: activeDivider === letter.id,
                            })}
                          />
                        </div>
                        {providedDroppable.placeholder}
                      </div>
                    )}
                  </Draggable>
                </div>
              )}
            </Droppable>
          </div>
        );
      }
      // render each letter and droppable zone between letters
      return (
        <div key={letter.id}>
          <div>
            <div className={wordItemClasses}>
              <p>{letter.content}</p>
            </div>
          </div>

          {index !== word.length - 1 && (
            <Droppable
              key={letter.id + "_" + index.toString()}
              isDropDisabled={
                letter.id.includes("divider") ||
                word[index + 1].id.includes("divider") ||
                currentPuzzleEnded
              }
              droppableId={letter.id}
            >
              {(providedDroppable, snapshot) => (
                <div
                  className={cx({
                    dropzone: true,
                    dragover: snapshot.isDraggingOver,
                    "selected-divider":
                      activeDivider &&
                      !isDragging &&
                      !word[index + 1].id.includes("divider"),
                    disabled: currentPuzzleEnded || !interactive,
                  })}
                  tabIndex={
                    activeDivider &&
                    !word[index + 1].id.includes("divider") &&
                    !currentPuzzleEnded &&
                    !(currentPuzzleEnded || !interactive)
                      ? 0
                      : -1
                  }
                  onMouseDown={() => {
                    handleMoveDivider(word, index);
                  }}
                  onPointerDown={() => {
                    handleMoveDivider(word, index);
                  }}
                  onKeyDown={(e) => {
                    handleKeyDownDrop(e, word, index);
                  }}
                  ref={providedDroppable.innerRef}
                  {...providedDroppable.droppableProps}
                  style={{
                    width:
                      snapshot.isDraggingOver ||
                      (dropzoneVisible &&
                        !word[index + 1].id.includes("divider"))
                        ? "40px"
                        : 0,
                    borderWidth:
                      snapshot.isDraggingOver ||
                      (dropzoneVisible &&
                        !word[index + 1].id.includes("divider"))
                        ? "initial"
                        : 0,
                  }}
                >
                  {activeDivider && (
                    <div className="active-divider-preview">
                      <Divider
                        visible={true}
                        dividerClasses={cx({
                          active: true,
                        })}
                      />
                    </div>
                  )}
                  {providedDroppable.placeholder}
                </div>
              )}
            </Droppable>
          )}
        </div>
      );
    });
  };

  const renderOption = () => {
    const optionContainerClasses = cx({
      option: true,
      disabled: currentPuzzleEnded,
    });

    const lastVisibleDivider = dividers.map((d) => d.visible).lastIndexOf(true);

    return (
      <Droppable droppableId={"dividers"} isDropDisabled={currentPuzzleEnded}>
        {(providedDroppable, snapshot) => (
          <div
            className={cx({
              dropzone: true,
              dragover: snapshot.isDraggingOver,
              "selected-divider": activeDivider && !isDragging,
              disabled: currentPuzzleEnded,
            })}
            onMouseDown={() => {
              handleMoveDivider(dividers);
            }}
            onPointerDown={() => {
              handleMoveDivider(dividers);
            }}
            onKeyDown={(e) => {
              handleKeyDownDrop(e, dividers);
            }}
            tabIndex={
              activeDivider && !(currentPuzzleEnded || !interactive) ? 0 : -1
            }
            ref={providedDroppable.innerRef}
            {...providedDroppable.droppableProps}
          >
            {dividers.map((divider, index) => (
              <div
                key={divider.id}
                className={optionContainerClasses}
                onMouseDown={handleOptionClick}
                onPointerDown={handleOptionClick}
              >
                <div
                  ref={(el) => {
                    if (el) allOptionsRef.current[index] = el;
                  }}
                >
                  <Draggable
                    // key={divider.id + index}
                    draggableId={divider.id}
                    index={index}
                    isDragDisabled={
                      !interactive || !divider.visible || currentPuzzleEnded
                    }
                  >
                    {(provided, draggableSnapshot) => (
                      <div
                        onPointerUp={() => {
                          onMouseTouchDown(divider.id);
                        }}
                        onKeyDown={(e) => {
                          handleKeyDown(e, divider.id);
                        }}
                        className={cx({
                          "letter-item": true,
                          disabled:
                            !interactive ||
                            !divider.visible ||
                            currentPuzzleEnded,
                        })}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        ref={provided.innerRef}
                        style={{
                          ...getDraggableStyle(
                            provided.draggableProps.style,
                            draggableSnapshot
                          ),
                          visibility: divider.visible ? "visible" : "hidden",
                        }}
                        tabIndex={
                          index !== lastVisibleDivider ||
                          currentPuzzleEnded ||
                          !interactive
                            ? -1
                            : 0
                        }
                      >
                        <Divider
                          visible={divider.visible}
                          dividerClasses={cx({
                            active: activeDivider === divider.id,
                          })}
                          preview={index === lastVisibleDivider && preview}
                        />
                      </div>
                    )}
                  </Draggable>
                </div>
              </div>
            ))}
            {activeDivider && (
              <div className="active-divider-preview">
                <Divider
                  visible={true}
                  dividerClasses={cx({
                    active: true,
                  })}
                />
              </div>
            )}
            {providedDroppable.placeholder}
          </div>
        )}
      </Droppable>
    );
  };

  return (
    <div
      className={`syllable-segmentation-container ${
        isVisible && mappedWord.length ? "visible" : ""
      }`}
    >
      <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
        <div className="letters-container" ref={lettersRef}>
          {renderTestWord(mappedWord)}
        </div>
        <div className="options-container">{renderOption()}</div>
      </DragDropContext>

      <SubmitBtn
        className={interactive ? "visible" : ""}
        onClick={submitAnswer}
        disabled={word.length === mappedWord.length}
      ></SubmitBtn>
    </div>
  );
}
